<template>
  <div>

    <modal v-if="showCreateModal" :hideHeader="true" @close="closeCreateModal">
      <template v-slot:body>
        <h3 class="modal-title">Create Personal API Key</h3>
        <p class="modal-description">
          Create a new API key for API access. The API Key is personal and
          connected to your user. Store it securely and do not share it, as
          it grants access to your CloudLabel resources.
        </p>

        <div class="modal-field">
          <v-input
            :labelPlaceholder="'API Key Name'"
            v-model="newKeyName"
            :editable="true"
          />
        </div>

        <div class="modal-field">
          <p class="field-label">Expiration</p>
          <drop-down
            placeholder="Select expiration"
            :value="selectedExpiration"
            valueProperty="label"
            :options="expirationOptions"
            @input="selectedExpiration = $event"
          />
        </div>

        <div class="modal-field" v-if="isCustomExpiration">
          <p class="field-label">Custom expiration date</p>
          <input
            type="date"
            class="date-input"
            :class="{ 'date-input-error': customDateError }"
            :min="minExpirationDate"
            v-model="customExpirationDate"
            @input="onDateInput"
          />
          <p v-if="customDateError" class="date-error">{{ customDateError }}</p>
        </div>

        <div class="modal-actions">
          <button class="button" type="button" :disabled="isCreating" @click="closeCreateModal">
            Cancel
          </button>
          <button
            class="button primary"
            type="button"
            :disabled="!canCreate || isCreating"
            @click="createKey"
          >
            <span v-if="isCreating" class="btn-loader"></span>
            {{ isCreating ? 'Creating...' : 'Create API Key' }}
          </button>
        </div>
      </template>
    </modal>

    <modal
      v-if="showRevokeConfirm"
      type="warning"
      heading="Revoke API Key"
      :description="`Are you sure you want to revoke '${activeKey ? activeKey.description : ''}'? It will stop working immediately and cannot be undone.`"
      @close="showRevokeConfirm = false"
    >
      <template v-slot:buttons>
        <div class="modal-actions">
          <button class="button" type="button" @click="showRevokeConfirm = false">
            Cancel
          </button>
          <button class="button primary" type="button" @click="confirmRevoke">
            Revoke Key
          </button>
        </div>
      </template>
    </modal>

    <wrapper class="mb-5">
      <div class="api-key-zone">

        <div class="zone-header">
          <h3 class="zone-title">API Key</h3>
          <p class="zone-ingress">
            If you are using the CloudLabel Agent and/or the Connector, you may
            need or be required to use an API key to communicate with the
            integrated CloudLabel solution. Manage your API key here.
          </p>
        </div>

        <p class="zone-section-label">Active API Key</p>

        <div v-if="!activeKey" class="no-key-state">
          <p class="no-key-text">
            No active API key. Create one to enable API access for the CloudLabel
            Agent and Connector integrations.
          </p>
          <button class="button primary mt-4" type="button" @click="showCreateModal = true">
            Create API Key
          </button>
        </div>

        <div v-else class="active-key-block">

          <div v-if="isNewlyCreated" class="one-time-banner">
            <svg class="banner-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              <strong>This key is personal and should be kept secure.</strong>
              Copy it and store it securely before leaving this page.
            </span>
          </div>

          <div class="key-info-row">
            <div>
              <p class="key-name">{{ activeKey.description }}</p>
              <p class="key-meta">
                Created {{ formatDate(activeKey.createdAt) }}
                <template v-if="activeKey.expirationDate">
                  &nbsp;·&nbsp; Expires {{ formatDate(activeKey.expirationDate) }}
                </template>
                <template v-else>
                  &nbsp;·&nbsp; No expiration
                </template>
              </p>
            </div>
            <button
              class="button revoke-btn"
              type="button"
              @click="showRevokeConfirm = true"
            >
              Revoke
            </button>
          </div>

          <div class="token-row">
            <div class="token-display">
              <code class="token-code">
                {{ showToken ? activeKey.key : maskedToken }}
              </code>
            </div>

            <button
              class="button small icon-action-btn"
              type="button"
              :title="showToken ? 'Hide token' : 'Show token'"
              @click="showToken = !showToken"
            >

              <svg v-if="!showToken" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>

              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
              </svg>
            </button>

            <button
              class="button small icon-action-btn"
              type="button"
              :title="copied ? 'Copied!' : 'Copy to clipboard'"
              @click="copyToken"
            >

              <svg v-if="!copied" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>

              <svg v-else class="copied-check" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="revokedKeys.length > 0" class="revoked-section">
          <p class="zone-section-label revoked-section-label">
            Revoked or Expired API Keys
          </p>
          <div
            v-for="key in revokedKeys"
            :key="key.id"
            class="revoked-key-row"
          >
            <div class="revoked-key-info">
              <p class="key-name">{{ key.description }}</p>
              <p class="key-meta">
                Created {{ formatDate(key.createdAt) }}
                <template v-if="key.expirationDate">
                  &nbsp;·&nbsp; Expires {{ formatDate(key.expirationDate) }}
                </template>
                <template v-else>
                  &nbsp;·&nbsp; No expiration
                </template>
                &nbsp;·&nbsp; Revoked {{ formatDate(key.revokedAt) }}
              </p>
            </div>
            <span class="revoked-badge">Revoked</span>
          </div>
        </div>

      </div>
    </wrapper>
  </div>
</template>

<script>
import vInput from "components/forms/Input.vue";
import DropDown from "components/forms/DropDown.vue";
import Modal from "components/Modal.vue";

function toUtcIsoDate(dateString) {
  if (!dateString) return null;
  return new Date(`${dateString}T00:00:00.000Z`).toISOString();
}

function calculateExpirationDate(option, customDate) {
  if (!option || option.value === "none") return null;
  if (option.value === "custom") return toUtcIsoDate(customDate);
  const monthOffsets = {
    "1month":  1,
    "3months": 3,
    "6months": 6,
    "1year":   12,
    "2years":  24,
    "5years":  60,
  };
  const d = new Date();
  d.setMonth(d.getMonth() + (monthOffsets[option.value] || 0));
  return d.toISOString();
}

export default {
  name: "ApiKeyZone",
  components: {
    vInput,
    DropDown,
    Modal,
  },
  data() {
    return {
      showCreateModal: false,
      showRevokeConfirm: false,
      newKeyName: "",
      selectedExpiration: null,
      customExpirationDate: "",
      isNewlyCreated: false,
      isCreating: false,
      showToken: false,
      copied: false,
      rawDateInput: "",
      expirationOptions: [
        { label: "1 month",  value: "1month"  },
        { label: "3 months", value: "3months" },
        { label: "6 months", value: "6months" },
        { label: "1 year",   value: "1year"   },
        { label: "2 years",  value: "2years"  },
        { label: "5 years",  value: "5years"  },
        { label: "None",     value: "none"    },
        { label: "Custom",   value: "custom"  },
      ],
    };
  },
  computed: {
    activeKey() {
      return this.$store.getters["API_KEYS/ACTIVE_KEY"];
    },
    revokedKeys() {
      return this.$store.getters["API_KEYS/REVOKED_KEYS"];
    },
    isCustomExpiration() {
      return this.selectedExpiration && this.selectedExpiration.value === "custom";
    },
    canCreate() {
      if (!this.newKeyName.trim()) return false;
      if (!this.selectedExpiration) return false;
      if (this.isCustomExpiration) {
        if (!this.customExpirationDate) return false;
        if (this.customExpirationDate < this.minExpirationDate) return false;
      }
      return true;
    },
    minExpirationDate() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split("T")[0];
    },
    customDateError() {
      const input = this.rawDateInput || this.customExpirationDate;
      if (!input) return null;

      // Check for partial or complete date that is in the past
      const minDate = this.minExpirationDate; // Format: YYYY-MM-DD (e.g., 2026-04-02)
      const minYear = parseInt(minDate.substring(0, 4), 10);
      const minMonth = parseInt(minDate.substring(5, 7), 10);
      const minDay = parseInt(minDate.substring(8, 10), 10);

      // Parse user input - could be partial (YYYY, YYYY-MM, or YYYY-MM-DD)
      const parts = input.split("-");
      const inputYear = parts[0] ? parseInt(parts[0], 10) : null;
      const inputMonth = parts[1] ? parseInt(parts[1], 10) : null;
      const inputDay = parts[2] ? parseInt(parts[2], 10) : null;

      // Validate year
      if (inputYear !== null && !isNaN(inputYear)) {
        if (inputYear < minYear) {
          return `Year must be ${minYear} or later`;
        }

        // Validate month (only if year matches minimum year)
        if (inputMonth !== null && !isNaN(inputMonth)) {
          if (inputYear === minYear && inputMonth < minMonth) {
            return `Date must be ${minDate} or later`;
          }

          // Validate day (only if year and month match minimum)
          if (inputDay !== null && !isNaN(inputDay)) {
            if (inputYear === minYear && inputMonth === minMonth && inputDay < minDay) {
              return `Date must be ${minDate} or later`;
            }
          }
        }
      }

      return null;
    },
    maskedToken() {
      return "cl_" + "•".repeat(44);
    },
  },
  methods: {
    async createKey() {
      if (!this.canCreate || this.isCreating) return;
      this.isCreating = true;
      const expiration = calculateExpirationDate(
        this.selectedExpiration,
        this.customExpirationDate
      );
      const key = {
        description: this.newKeyName.trim(),
        expirationTest: this.selectedExpiration.label,
        expiration,
      };
      try {
        await this.$store.dispatch("API_KEYS/CREATE_KEY", key);
        this.isNewlyCreated = true;
        this.showToken = true;
        this.closeCreateModal();
      } catch (_error) {
        // Keep modal open when create fails so user can retry.
      } finally {
        this.isCreating = false;
      }
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.newKeyName = "";
      this.selectedExpiration = null;
      this.customExpirationDate = "";
      this.rawDateInput = "";
    },
    async confirmRevoke() {
      try {
        await this.$store.dispatch("API_KEYS/REVOKE_KEY");
        this.isNewlyCreated = false;
        this.showToken = false;
        this.showRevokeConfirm = false;
      } catch (_error) {
        // Keep confirmation open when revoke fails so user can retry.
      }
    },
    copyToken() {
      if (!this.activeKey) return;
      const token = this.activeKey.key;
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(token).then(() => this.flashCopied());
      } else {
        const el = document.createElement("textarea");
        el.value = token;
        el.style.cssText = "position:absolute;opacity:0;pointer-events:none;";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        this.flashCopied();
      }
    },
    flashCopied() {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    },
    onDateInput(event) {
      this.rawDateInput = event.target.value;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch("API_KEYS/GET_ACTIVE_KEY");
    } catch (_error) {
      // Keep the view usable even if active key fetch fails.
    }
  },
};
</script>

<style scoped lang="postcss">
/* ── Zone layout ─────────────────────────────────────────── */
.api-key-zone {
  padding: 0.25rem 0;
}

.zone-header {
  margin-bottom: 1.5rem;
}

.zone-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: theme("colors.textcolor");
  margin-bottom: 0.4rem;
}

.zone-ingress {
  font-size: 0.85rem;
  font-style: italic;
  color: theme("colors.darkendtextcolor");
  line-height: 1.6;
}

/* ── Section labels ──────────────────────────────────────── */
.zone-section-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: theme("colors.highlight");
  margin-bottom: 0.875rem;
}

.revoked-section-label {
  color: theme("colors.darkendtextcolor");
}

/* ── No-key state ────────────────────────────────────────── */
.no-key-state {
  padding-bottom: 0.5rem;
}

.no-key-text {
  font-size: 0.875rem;
  color: theme("colors.darkendtextcolor");
  max-width: 520px;
  line-height: 1.5;
}

/* ── Active key block ────────────────────────────────────── */
.active-key-block {
  padding-bottom: 0.25rem;
}

/* One-time banner */
.one-time-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 0.375rem;
  background: rgba(245, 145, 0, 0.1);
  border-left: 3px solid theme("colors.highlight");
  font-size: 0.85rem;
  color: theme("colors.textcolor");
  line-height: 1.5;
}

.banner-icon {
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
  margin-top: 0.15rem;
  color: theme("colors.highlight");
}

/* Key info row */
.key-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.key-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: theme("colors.textcolor");
  margin: 0;
}

.key-meta {
  font-size: 0.75rem;
  color: theme("colors.darkendtextcolor");
  margin: 0.2rem 0 0;
}

/* Revoke button – danger styling */
.revoke-btn {
  color: #d9534f !important;
  border-color: #d9534f !important;
  font-size: 0.8125rem;
  padding: 0.3rem 0.9rem !important;
  flex-shrink: 0;
  transition: border-color 0.15s, color 0.15s;
}

.revoke-btn:hover {
  color: #e87777 !important;
  border-color: #e87777 !important;
}

/* Token row */
.token-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.token-display {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  background: theme("colors.secondary");
  overflow: hidden;
}

.token-code {
  font-family: "Courier New", Courier, monospace;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  color: theme("colors.darkendtextcolor");
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Icon action buttons (show/hide, copy) */
.icon-action-btn {
  width: 2.1rem;
  height: 2.1rem;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-color: theme("colors.primary-light") !important;
  transition: border-color 0.15s;
}

.icon-action-btn svg {
  width: 0.9375rem;
  height: 0.9375rem;
  color: theme("colors.darkendtextcolor");
  transition: color 0.15s;
}

.icon-action-btn:hover {
  border-color: theme("colors.highlight") !important;
}

.icon-action-btn:hover svg {
  color: theme("colors.highlight");
}

.copied-check {
  color: theme("colors.highlight") !important;
}

/* ── Revoked section ─────────────────────────────────────── */
.revoked-section {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid theme("colors.primary-light");
}

.revoked-key-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0;
  border-bottom: 1px solid rgba(37, 90, 128, 0.35);
}

.revoked-key-row:last-child {
  border-bottom: none;
}

.revoked-key-info {
  flex: 1;
  min-width: 0;
}

.revoked-badge {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.2rem 0.55rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.06);
  color: theme("colors.darkendtextcolor");
  flex-shrink: 0;
  margin-left: 0.75rem;
}

/* ── Modal styles ────────────────────────────────────────── */
.modal-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
  color: theme("colors.textcolor");
}

.modal-description {
  font-size: 0.85rem;
  color: theme("colors.darkendtextcolor");
  line-height: 1.55;
  margin-bottom: 1.5rem;
}

.modal-field {
  margin-bottom: 1.25rem;
}

.field-label {
  font-size: 0.8125rem;
  color: theme("colors.darkendtextcolor");
  margin-bottom: 0.375rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

/* Input text inside modal — override white text that's invisible on the light modal bg */
.modal-field ::v-deep input {
  color: theme("colors.textcolor-dark");
}

/* Native date input – matches existing form underline style */
.date-input {
  width: 100%;
  padding: 0.5rem 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid theme("colors.primary-light");
  color: theme("colors.textcolor");
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.2s;
}

.date-input:focus {
  border-bottom-color: theme("colors.highlight");
}

.date-input-error {
  border-bottom-color: #d9534f !important;
}

.date-error {
  color: #d9534f;
  font-size: 0.75rem;
  margin-top: 0.375rem;
  margin-bottom: 0;
}

.date-input::-webkit-calendar-picker-indicator {
  filter: invert(0.7);
  cursor: pointer;
}

/* Button loader spinner */
.btn-loader {
  display: inline-block;
  width: 0.875rem;
  height: 0.875rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 0.5rem;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
